import {
  Button,
  Card,
  Col,
  DatePicker,
  Radio,
  Row,
  Select,
  Input,
  Modal,
  Form,
  AutoComplete,
  Alert,
  Switch,
  Tooltip,
  Typography,
  Divider,
} from "antd";
import { get, isEmpty, map, keyBy } from "lodash";
import moment from "moment-timezone";
import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useApolloClient, useQuery } from "@apollo/client";
import { GET_ALL_REQUIREMENTS } from "../../../graphql/requirements";

import { DocumentPreview } from "../../../components/documentPreview";
import {
  DocumentStatus,
  MISCELLANEOUS_APPROVAL_WARNING_MESSAGE,
} from "../../../constants/documents";
import { checkIsMiscellaneousApprovalRestricted } from "../../../utils/documents";
import { FullScreenModal } from "./style";
import { ImageViewer } from "../../../components/imageViewer";
import { GET_DOCUMENT_LIST } from "./gql";
import { useDeleteWorkerDocument } from "@src/appV2/WorkerDocuments/api/useDeleteWorkerDocument";
import { GET_HCP_PENDING_DOCS_COUNT } from "../../accountManagement/agents/gql";
import {
  EXPIRY_TYPE,
  DRIVING_LICENSE_STATE_ID_REQUIREMENT_ID,
} from "src/containers/requirements/constants";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { logEvent, APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { isDefined } from "@clipboard-health/util-ts";
import { DocumentZendeskTickets } from "@src/appV2/WorkerDocuments/components/DocumentZendeskTickets";
import { useUpdateWorkerDocumentV2 } from "@src/appV2/WorkerDocuments/api/useUpdateWorkerDocumentV2";
import {
  buildUpdateDocumentV2Payload,
  isHcpDocumentStatusValue,
} from "./buildUpdateDocumentV2Payload";
import { useGetRejectionMappings } from "./useGetRejectionMappings";
import { useGetDocumentEvidences } from "./useGetDocumentEvidences";
import { EvidenceFormView } from "@src/appV2/redesign/Evidence/components/EvidenceForm/EvidenceFormView";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { useEvidenceForm } from "@src/appV2/redesign/Evidence/components/EvidenceForm/useEvidenceForm";
import { getErrorMessage } from "@src/appV2/api/errors";

const { Item: FormItem } = Form;
const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const InfoVariableDescription = (
  <div>
    <ol>
      <li>{"{{documentName}}"} - will be replaced by the actual document name </li>
      <li>{"{{reason}}"} - will be replaced by the actual reason selected </li>
    </ol>
  </div>
);

// @ts-expect-error TODO(typecheck-unify): TS7031 Binding element 'visible' implicitly has an 'any' type.
function Component({ visible, document, onCancel, onUpdated, agentTMZ }) {
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [documentInfo, setDocumentInfo] = useState<any>({});
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedRequirement, setSelectedRequirement] = useState<any>({});
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [notification, setNotification] = useState<any>({});
  const [isUpdating, setUpdating] = useState(false);
  const [isDeleting, setDeleting] = useState(false);
  const [imagePath, setImagePath] = useState<string>();
  const { data, loading } = useQuery(GET_ALL_REQUIREMENTS);
  const requirements = data && keyBy(data.allRequirements, "_id");
  const { mutateAsync: deleteWorkerDocument } = useDeleteWorkerDocument();
  const apolloClient = useApolloClient();

  const { data: rejectionMappingsResponse, isLoading: isRejectionMappingsLoading } =
    useGetRejectionMappings({
      staleTime: 5 * 60 * 1000,
    });

  const docsV2ToolsFlag = useCbhFlag(CbhFeatureFlag.DOCS_V2_TOOLS_IN_ADMIN_UI, {
    defaultValue: { isEnabled: false },
  });
  const isDocsV2Enabled = docsV2ToolsFlag.isEnabled;

  const {
    evidences: documentEvidences,
    includedDefinitions,
    isLoading: isEvidenceLoading,
    isError: isEvidenceFetchError,
  } = useGetDocumentEvidences(isDocsV2Enabled ? (document?._id ?? "") : "");
  const { mutateAsync: updateDocumentV2 } = useUpdateWorkerDocumentV2();

  const evidenceForDocument = useMemo(
    () => (isDocsV2Enabled && documentEvidences.length === 1 ? documentEvidences[0] : undefined),
    [isDocsV2Enabled, documentEvidences]
  );

  // The evidence's linked definition, hydrated by id from the same fetch's
  // `included` array (include=evidenceDefinition) — no separate catalog fetch.
  const linkedDefinitionId = evidenceForDocument?.relationships.evidenceDefinition.data.id;
  const linkedDefinition = useMemo(
    () => includedDefinitions.find((definition) => definition.id === linkedDefinitionId),
    [includedDefinitions, linkedDefinitionId]
  );

  const { viewProps, snapshot, validate, getCleanedProperties } = useEvidenceForm({
    evidence: evidenceForDocument,
    linkedDefinition,
    documentStatus: documentInfo.status,
  });

  // Show error and close if multiple evidences found for document
  useEffect(() => {
    if (isDocsV2Enabled && documentEvidences.length > 1) {
      showErrorToast("Multiple evidences found for this document. Please contact support.");
      onCancel();
    }
  }, [isDocsV2Enabled, documentEvidences, onCancel]);

  useEffect(() => {
    if (loading) {
      return;
    }

    initDocumentInfo();

    // set default notification value
    setNotification({
      note: "",
      reason: "",
      pushNotification: document?.status !== DocumentStatus.REJECTED,
    });
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, loading]);

  useEffect(() => {
    setSelectedRequirement(requirements ? requirements[documentInfo.category] : {});
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentInfo.category]);

  useEffect(() => {
    if (!selectedRequirement?.visibleToHCP) {
      setNotification({ ...notification, pushNotification: false });
    }
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequirement]);

  const initDocumentInfo = () => {
    if (!document) {
      setDocumentInfo({});
      return;
    }
    setDocumentInfo({
      ...document,
      category: document.fulfilledRequirement?._id || null,
      expiryType: get(requirements, `${document.fulfilledRequirement?._id}.expiryType`, null),
      expiry: document.expiry && moment(document.expiry),
      issueDate: document.issueDate && moment(document.issueDate),
      displayName: document?.fulfilledRequirement?.name,
      subType: document?.subType || "",
    });
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'key' implicitly has an 'any' type.
  const onChange = (key) => (value) => setDocumentInfo((state) => ({ ...state, [key]: value }));

  const handleReasonSelection = (
    selectedReason: string,
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rejectionMappingsData: any[] | undefined
  ) => {
    if (!selectedReason || !rejectionMappingsData) {
      return;
    }

    const rejectionMapping = rejectionMappingsData.find(
      (mapping) => mapping.attributes.title === selectedReason
    );

    const pushNotificationTemplate = rejectionMapping?.attributes.pushNotificationTemplate;
    if (pushNotificationTemplate) {
      const autoFilledNote = pushNotificationTemplate
        .replace(/{{documentName}}/g, documentInfo.displayName || "document")
        .replace(/{{reason}}/g, selectedReason);

      if (isDefined(autoFilledNote)) {
        // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'prev' implicitly has an 'any' type.
        setNotification((prev) => ({ ...prev, note: autoFilledNote }));
      }
    }

    if (isDefined(rejectionMapping?.attributes.pushNotificationEnabled)) {
      // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'prev' implicitly has an 'any' type.
      setNotification((prev) => ({
        ...prev,
        pushNotification: rejectionMapping.attributes.pushNotificationEnabled,
      }));
    }
  };

  const onRejectionInfoChange =
    // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'key' implicitly has an 'any' type.
    (key) =>
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-shadow
      (data: string | boolean | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> = "") => {
        let value: string | boolean;
        if (typeof data === "boolean") {
          value = data;
        } else if (typeof data === "object" && data.target) {
          value = data.target.value;
        } else {
          value = data as string;
        }
        setNotification({ ...notification, [key]: value });
      };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'callback' implicitly has an 'any' type.
  const onCategoryChange = (callback) => (value) => {
    callback(value);
    const displayName = get(requirements, `${value}.name`, "");
    const expiryType = get(requirements, `${value}.expiryType`, null);
    // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'state' implicitly has an 'any' type.
    setDocumentInfo((state) => ({ ...state, displayName, expiryType }));
  };

  const handleEmptyDisplayName = () => {
    const name = documentInfo.uploadedFor?.name;
    if (documentInfo.displayName || !name) {
      return;
    }

    // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'state' implicitly has an 'any' type.
    setDocumentInfo((state) => ({ ...state, displayName: name }));
  };

  // cspell:ignore Emptycategory
  const handleEmptycategory = () => {
    const _id = documentInfo.uploadedFor?._id;
    if (!isEmpty(documentInfo.category) || !_id) {
      return;
    }

    // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'state' implicitly has an 'any' type.
    setDocumentInfo((state) => ({ ...state, category: String(_id) }));
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'callback' implicitly has an 'any' type.
  const onExpiryChange = (callback) => (value) => {
    callback(value);
    handleEmptyDisplayName();
    handleEmptycategory();
  };

  const onInputChange =
    // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'key' implicitly has an 'any' type.
    (key) =>
      // @ts-expect-error TODO(typecheck-unify): TS7031 Binding element 'target' implicitly has an 'any' type.
      ({ target }) => {
        const newValue = target.value;

        if (key === "status") {
          const currentStatus = documentInfo.status;
          const newStatus = newValue;

          const statusesWithNotification = [DocumentStatus.REJECTED, DocumentStatus.PENDING];
          const isCurrentStatusWithNotification = statusesWithNotification.includes(currentStatus);
          const isNewStatusWithNotification = statusesWithNotification.includes(newStatus);

          if (
            isCurrentStatusWithNotification &&
            isNewStatusWithNotification &&
            currentStatus !== newStatus
          ) {
            setNotification({
              reason: "",
              note: "",
              pushNotification: false,
            });
          } else if (isCurrentStatusWithNotification && !isNewStatusWithNotification) {
            setNotification({
              reason: "",
              note: "",
              pushNotification: false,
            });
          }
        }

        onChange(key)(newValue);
      };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'uploadInfo' implicitly has an 'any' type.
  const showDisplayNameError = (uploadInfo) => {
    if (!uploadInfo.displayName) {
      showErrorToast("Please enter display name");
      return true;
    }
    return false;
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'uploadInfo' implicitly has an 'any' type.
  const showExpiryError = (uploadInfo) => {
    const isNewDocumentApproved = uploadInfo.status === DocumentStatus.APPROVED;
    // cspell:ignore Fullfilled
    const isNewDocumentFullfilledRequirementPresent = isDefined(uploadInfo.fulfilledRequirementId);
    const isNewDocumentExpiryPresent = isDefined(uploadInfo.expiry) && !isEmpty(uploadInfo.expiry);
    const newDocumentHasExpiryMissing =
      isNewDocumentApproved &&
      isNewDocumentFullfilledRequirementPresent &&
      !isNewDocumentExpiryPresent;
    const isExpiryRemoved =
      isDefined(document.expiry) && !isEmpty(document.expiry) && !isNewDocumentExpiryPresent;
    const isNonExpiringRequirement =
      requirements[documentInfo.category]?.expiryType === EXPIRY_TYPE.DOES_NOT_EXPIRE;

    if (!isNonExpiringRequirement && (newDocumentHasExpiryMissing || isExpiryRemoved)) {
      showErrorToast("Please enter expiry");
      return true;
    }
    return false;
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'uploadInfo' implicitly has an 'any' type.
  const showUploadValidationError = (uploadInfo) => {
    return showDisplayNameError(uploadInfo) || showExpiryError(uploadInfo);
  };

  const getUploadInfo = () => ({
    id: documentInfo._id,
    fulfilledRequirementId: documentInfo.category ?? undefined,
    displayName: documentInfo.displayName,
    status: documentInfo.status,
    expiry:
      !documentInfo.expiry ||
      requirements[documentInfo.category]?.expiryType === EXPIRY_TYPE.DOES_NOT_EXPIRE
        ? null
        : moment.tz(documentInfo.expiry, agentTMZ).endOf("day").toISOString(),
    issueDate: documentInfo.issueDate?.toISOString(),
    ...((documentInfo.status === DocumentStatus.REJECTED ||
      documentInfo.status === DocumentStatus.PENDING) && { notification }),
    subType: isPersonalId ? documentInfo?.subType : "",
  });

  const refetchPendingDocsQueries = async () => {
    try {
      await apolloClient.refetchQueries({
        include: [GET_DOCUMENT_LIST, GET_HCP_PENDING_DOCS_COUNT],
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showErrorToast(error.message);
    }
  };

  const validateTemplateVariables = (noteText: string): string[] => {
    const templateVariableRegex = /\{\{([^}]+)\}\}/g;
    const matches: string[] = [];
    let match: RegExpExecArray | null;

    while ((match = templateVariableRegex.exec(noteText)) !== null) {
      matches.push(match[1]);
    }

    return matches;
  };

  const logDocumentReviewSuccess = (uploadInfo: ReturnType<typeof getUploadInfo>) => {
    const newStatus = uploadInfo.status;
    let event: string | undefined;
    if (newStatus === DocumentStatus.APPROVED) {
      event = APP_V2_APP_EVENTS.AGENT_DRAWER_DOCUMENT_APPROVED;
    } else if (newStatus === DocumentStatus.REJECTED) {
      event = APP_V2_APP_EVENTS.AGENT_DRAWER_DOCUMENT_REJECTED;
    } else if (newStatus === DocumentStatus.PENDING) {
      event = APP_V2_APP_EVENTS.AGENT_DRAWER_DOCUMENT_MARKED_PENDING;
    }
    if (!event) {
      return;
    }
    logEvent(event, {
      workerId: documentInfo.hcpId,
      documentId: documentInfo._id,
      documentDisplayName: documentInfo.displayName,
      requirementId: documentInfo.category,
      requirementName: requirements?.[documentInfo.category]?.name,
      originalRequirementId: document?.fulfilledRequirement?._id,
      newStatus,
      expiry: uploadInfo.expiry,
      subType: uploadInfo.subType,
      notificationReason: notification?.reason,
      notificationPushNotification: notification?.pushNotification,
      evidenceId: evidenceForDocument?.id,
      evidenceDefinitionId: snapshot.evidenceDefinitionId || undefined,
    });
  };

  // @ts-expect-error TODO(typecheck-unify): TS7030 Not all code paths return a value.
  const onUpdate = async () => {
    // Rejection: a reason is always required (canonical review-field rules), and a
    // note to HCP is required only when notifying. This holds regardless of the
    // "Notify HCP" toggle, including its reachable off states (already-rejected doc
    // on load, requirement not visibleToHCP, status-change reset, or manual toggle).
    if (documentInfo?.status === DocumentStatus.REJECTED) {
      if (!notification.reason?.trim()) {
        return showErrorToast("You must enter a rejection reason");
      }
      if (notification.pushNotification && !notification.note?.trim()) {
        return showErrorToast("You must enter a note to HCP");
      }
    }

    // Pending (in review): unchanged — a reason and note are required only when notifying HCP.
    if (
      documentInfo?.status === DocumentStatus.PENDING &&
      notification.pushNotification &&
      (!notification.reason?.trim() || !notification.note?.trim())
    ) {
      return showErrorToast("You must enter a pending reason and a note to HCP");
    }

    if (
      (documentInfo?.status === DocumentStatus.REJECTED ||
        documentInfo?.status === DocumentStatus.PENDING) &&
      notification.pushNotification &&
      notification.note?.trim()
    ) {
      const unreplacedVariables = validateTemplateVariables(notification.note);
      if (unreplacedVariables.length > 0) {
        return Modal.error({
          title: "Template Variables Found",
          content: (
            <div>
              <p>Please replace the following template variables in your note before submitting:</p>
              <ul>
                {unreplacedVariables.map((variable, index) => (
                  // FIXME: Automatically disabled during ESLint config update
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={index}>
                    <code>{`{{${variable}}}`}</code>
                  </li>
                ))}
              </ul>
              <p>Replace these variables with actual values or remove them from your note.</p>
            </div>
          ),
        });
      }
    }

    const uploadInfo = getUploadInfo();
    if (showUploadValidationError(uploadInfo)) {
      return;
    }

    if (snapshot.isEvidenceApproved && snapshot.isUnknownDefinitionSelected) {
      showErrorToast("Please select a known evidence definition before approving");
      return;
    }

    if (snapshot.isEvidenceApproved) {
      const isEvidenceFormValid = await validate();
      if (!isEvidenceFormValid) {
        showErrorToast("Please fill in all required evidence fields");
        return;
      }
    }

    if (!isHcpDocumentStatusValue(uploadInfo.status)) {
      showErrorToast(`Cannot update: unsupported document status "${String(uploadInfo.status)}"`);
      return;
    }

    setUpdating(true);

    const cleanedProperties = getCleanedProperties();
    const body = buildUpdateDocumentV2Payload({
      uploadInfo,
      evidenceDefinitionId: snapshot.evidenceDefinitionId,
      hasLinkedEvidence: snapshot.hasLinkedEvidence,
      isEvidenceApproved: snapshot.isEvidenceApproved,
      cleanedProperties,
    });

    try {
      await updateDocumentV2({ id: documentInfo._id, body });
      await refetchPendingDocsQueries();
    } catch (error: unknown) {
      logEvent(APP_V2_USER_EVENTS.HCP_DOCUMENT_V2_UPDATE_FAILED, {
        documentId: documentInfo._id,
        evidenceId: evidenceForDocument?.id,
      });
      showErrorToast(getErrorMessage(error, "An unexpected error occurred. Please try again."));
      setUpdating(false);
      return;
    }

    logDocumentReviewSuccess(uploadInfo);
    setUpdating(false);
    onUpdated();
    onCancel();
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await deleteWorkerDocument({ id: documentInfo._id });
      await refetchPendingDocsQueries();
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showErrorToast(error.message);
    }
    setDeleting(false);
    onCancel();
  };

  const handleImageViewerClose = () => {
    setImagePath("");
  };

  const confirmDelete = () => {
    Modal.confirm({
      title: `Are you sure you want to delete this document?`,
      onOk: onDelete,
    });
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006,TS7031 Parameter 'input' implicitly has an 'any' type.
  const filterRequirements = (input, { props }) =>
    props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  const isPersonalId = useMemo(() => {
    if (!documentInfo.category) {
      return false;
    }

    return String(documentInfo.category) === DRIVING_LICENSE_STATE_ID_REQUIREMENT_ID;
  }, [documentInfo]);

  const isMiscellaneousApprovalRestricted = useMemo(
    () =>
      checkIsMiscellaneousApprovalRestricted(
        requirements?.[documentInfo.category]?.name,
        documentInfo.status
      ),
    [documentInfo.category, documentInfo.status, requirements]
  );

  const personalVerificationDocumentOptions = useCbhFlag(CbhFeatureFlag.PERSONAL_ID_SUBTYPES, {
    defaultValue: {
      manual: [],
      stripe: [],
    },
  });

  // Evidence-side fetch state blocks submit when the evidence lookup itself is
  // unresolved (loading or error) — we don't yet know if this document has linked
  // evidence, so allowing submit could produce a doc-only update that silently
  // skips the linked evidence. The linked definition rides along in the same
  // fetch (include=evidenceDefinition), so there is no separate definitions fetch
  // to wait on.
  const isEvidenceBlockingSubmit = isDocsV2Enabled && (isEvidenceLoading || isEvidenceFetchError);

  const isUpdateDisabled =
    isMiscellaneousApprovalRestricted ||
    isEvidenceBlockingSubmit ||
    // Block submit while the evidence-definition picker has an uncommitted search:
    // the box shows text that differs from the committed selection, and submitting
    // would silently save the prior selection instead of what's on screen.
    (isDocsV2Enabled && snapshot.isDefinitionSelectionPending) ||
    (isDocsV2Enabled && snapshot.isApprovalBlocked);

  return (
    <FullScreenModal visible={visible} title="Review Document" footer={null} onCancel={onCancel}>
      <Row style={{ height: "100%" }}>
        <Col span={12} style={{ height: "100%" }}>
          <ImageViewer
            visible={!!imagePath}
            images={[{ src: `${imagePath}`, alt: "Document" }]}
            onClose={handleImageViewerClose}
          />
          {/* @ts-expect-error FIXME this wrong type */}
          <DocumentPreview doc={documentInfo} setImagePath={setImagePath} isPreviewImage={true} />
        </Col>
        <Col span={12} style={{ height: "100%" }}>
          <Card style={{ width: "100%", height: "100%", overflow: "scroll" }} title="">
            <Form layout="vertical">
              <FormItem label="Category / Requirement">
                <Select
                  placeholder="Select Requirements"
                  optionFilterProp="children"
                  showSearch
                  onChange={onCategoryChange(onChange("category"))}
                  /* @ts-expect-error FIXME this wrong type */
                  filterOption={filterRequirements}
                  value={documentInfo.category}
                  loading={loading}
                >
                  {map(requirements, (requirement) => (
                    <Option key={requirement._id}>{requirement.name}</Option>
                  ))}
                </Select>
              </FormItem>
              {isPersonalId && (
                <FormItem label="ID type">
                  <Select
                    placeholder="choose type"
                    optionFilterProp="children"
                    labelInValue
                    data-testid="personal-id-subtype"
                    optionLabelProp="label"
                    value={documentInfo.subType || undefined}
                    onChange={(e) => {
                      // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'state' implicitly has an 'any' type.
                      setDocumentInfo((state) => ({
                        ...state,
                        subType: e.value,
                      }));
                    }}
                  >
                    {map(personalVerificationDocumentOptions.stripe, (option) => (
                      <Option
                        data-testid={`personal-id-option-${option}`}
                        key={option}
                        value={option}
                        label={option}
                      >
                        {option}
                      </Option>
                    ))}
                    {map(personalVerificationDocumentOptions.manual, (option, index) => {
                      const isFirstElement = index === 0;

                      return (
                        <Option
                          data-testid={`personal-id-option-${option}`}
                          key={option}
                          value={option}
                          label={option}
                        >
                          {option}
                          {isFirstElement && (
                            <Divider
                              style={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                margin: 0,
                              }}
                            ></Divider>
                          )}
                        </Option>
                      );
                    })}
                  </Select>
                </FormItem>
              )}
              {documentInfo.expiryType === EXPIRY_TYPE.DOES_NOT_EXPIRE && (
                <FormItem label="Expiry">
                  <Text>Does not expire</Text>
                </FormItem>
              )}

              {documentInfo.expiryType === EXPIRY_TYPE.ISSUE_PLUS_VALIDITY && (
                <FormItem label="Date of Document">
                  <DatePicker
                    value={documentInfo.issueDate}
                    format="MM/DD/YYYY"
                    onChange={onExpiryChange(onChange("issueDate"))}
                  />
                </FormItem>
              )}

              {(!documentInfo.expiryType ||
                documentInfo.expiryType === EXPIRY_TYPE.EXPIRY_DATE) && (
                <FormItem label="Expiry">
                  <DatePicker
                    value={documentInfo.expiry}
                    format="MM/DD/YYYY"
                    onChange={onExpiryChange(onChange("expiry"))}
                  />
                </FormItem>
              )}

              <FormItem label="Status">
                <Radio.Group
                  value={documentInfo.status}
                  buttonStyle="solid"
                  onChange={onInputChange("status")}
                >
                  <Radio.Button value={DocumentStatus.PENDING}>Needs Review</Radio.Button>
                  <Radio.Button value={DocumentStatus.APPROVED}>Approved</Radio.Button>
                  <Radio.Button value={DocumentStatus.REJECTED}>Rejected</Radio.Button>
                </Radio.Group>
                {isMiscellaneousApprovalRestricted && (
                  <div style={{ marginTop: "8px" }}>
                    <Text type="danger">{MISCELLANEOUS_APPROVAL_WARNING_MESSAGE}</Text>
                  </div>
                )}
              </FormItem>

              {documentInfo?.status === DocumentStatus.REJECTED && (
                <Card className={"notification-container"}>
                  <FormItem label="Reason for Rejection">
                    <AutoComplete
                      style={{ width: 200 }}
                      options={
                        rejectionMappingsResponse?.data.map((mapping) => ({
                          value: mapping.attributes.title,
                        })) ?? []
                      }
                      value={notification.reason}
                      placeholder={isRejectionMappingsLoading ? "Loading..." : "Reason"}
                      filterOption={(inputValue, option) =>
                        String(option?.value).toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                      }
                      allowClear={true}
                      onChange={(selectedReason) => {
                        setNotification({ ...notification, reason: selectedReason });
                        handleReasonSelection(
                          selectedReason,
                          rejectionMappingsResponse?.data || []
                        );
                      }}
                    />
                  </FormItem>
                  <FormItem label="Note to HCP">
                    <TextArea
                      rows={4}
                      disabled={!notification.pushNotification}
                      value={notification.note}
                      onChange={onRejectionInfoChange("note")}
                    />
                  </FormItem>

                  <FormItem name="a">
                    <div className={"note-container"}>
                      <p>Notify HCP</p>
                      <Tooltip
                        title={
                          !selectedRequirement?.visibleToHCP
                            ? "The document is not visible to HCP"
                            : ""
                        }
                        trigger="hover"
                      >
                        <Switch
                          onChange={onRejectionInfoChange("pushNotification")}
                          checked={notification.pushNotification}
                          disabled={!selectedRequirement?.visibleToHCP}
                        />
                      </Tooltip>
                    </div>
                  </FormItem>

                  {notification.pushNotification && (
                    <Alert
                      message="Available Variables"
                      description={InfoVariableDescription}
                      type="info"
                    />
                  )}
                </Card>
              )}

              {documentInfo?.status === DocumentStatus.PENDING && (
                <Card className={"notification-container"}>
                  <FormItem label="Reason for Pending Review">
                    <AutoComplete
                      style={{ width: 200 }}
                      options={
                        rejectionMappingsResponse?.data.map((mapping) => ({
                          value: mapping.attributes.title,
                        })) ?? []
                      }
                      value={notification.reason}
                      placeholder={isRejectionMappingsLoading ? "Loading..." : "Reason"}
                      filterOption={(inputValue, option) =>
                        String(option?.value).toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                      }
                      allowClear={true}
                      onChange={(selectedReason) => {
                        setNotification({ ...notification, reason: selectedReason });
                        handleReasonSelection(
                          selectedReason,
                          rejectionMappingsResponse?.data || []
                        );
                      }}
                    />
                  </FormItem>
                  <FormItem label="Note to HCP">
                    <TextArea
                      rows={4}
                      disabled={!notification.pushNotification}
                      value={notification.note}
                      onChange={onRejectionInfoChange("note")}
                    />
                  </FormItem>

                  <FormItem name="a">
                    <div className={"note-container"}>
                      <p>Notify HCP</p>
                      <Tooltip
                        title={
                          !selectedRequirement?.visibleToHCP
                            ? "The document is not visible to HCP"
                            : ""
                        }
                        trigger="hover"
                      >
                        <Switch
                          onChange={onRejectionInfoChange("pushNotification")}
                          checked={notification.pushNotification}
                          disabled={!selectedRequirement?.visibleToHCP}
                        />
                      </Tooltip>
                    </div>
                  </FormItem>

                  {notification.pushNotification && (
                    <Alert
                      message="Available Variables"
                      description={InfoVariableDescription}
                      type="info"
                    />
                  )}
                </Card>
              )}
              <DocumentZendeskTickets documentId={documentInfo._id} />
              {isDocsV2Enabled && isEvidenceFetchError && <Divider />}
              {isDocsV2Enabled && isEvidenceFetchError && (
                <Alert
                  type="warning"
                  message="Failed to load evidence data. Submit is disabled until it loads — please retry."
                  showIcon
                />
              )}
              {evidenceForDocument && documentInfo.status === DocumentStatus.APPROVED && (
                <BerlinThemeProvider>
                  <EvidenceFormView
                    {...viewProps}
                    isError={isEvidenceFetchError}
                    isLoading={isEvidenceLoading}
                  />
                </BerlinThemeProvider>
              )}
              <FormItem className="align-center">
                <Button
                  type="primary"
                  size="large"
                  onClick={onUpdate}
                  loading={isUpdating}
                  disabled={isUpdateDisabled}
                >
                  Update
                </Button>
                <Button
                  /* @ts-expect-error FIXME this wrong type */
                  type="danger"
                  style={{ marginLeft: "8px" }}
                  size="large"
                  onClick={confirmDelete}
                  loading={isDeleting}
                >
                  Delete
                </Button>
              </FormItem>
            </Form>
          </Card>
        </Col>
      </Row>
    </FullScreenModal>
  );
}

// @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'props' implicitly has an 'any' type.
function ReviewModal(props) {
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isAdmin = useSelector((state: any) => state.session?.admin);

  return isAdmin ? <Component {...props} /> : null;
}

export { ReviewModal };
